import React from 'react';
import { EditableColumnType } from 'components/CustomTable/CustomTable';
import AdditionalInformation from 'components/AdditionalInformation/AdditionalInformation';
import i18nIsoCountries from 'i18n-iso-countries';
import { DataType } from 'store/ducks/DataEntry/Branch/types';
import CustomSelect from 'components/CustomSelect';
import enLocale from 'i18n-iso-countries/langs/en.json';
import { DatePicker, Input } from 'antd';
import dayjs from 'dayjs';
import { ITPP } from 'store/ducks/DataEntry/ThirdPartyProviders/types';
import * as Styled from '../Tables/DataEntry/DataEntry.styled';

export const getTPPColumns = (
  handleSave: (record: ITPP) => void,
  countries: { value: string; label: string }[],
  providerCodeTypes: { value: string; label: string }[],
  substitutability: { value: string; label: string }[],
  doraRegCriticality: { value: string; label: string }[],
  alternatives: { value: string; label: string }[],
  natures: { value: string; label: string }[],
  possibility: { value: string; label: string }[],
  localRegCriticality: { value: string; label: string }[],
  nonSubstReasons: { value: string; label: string }[],
  personTypes: { value: string; label: string }[],
  t: any,
): EditableColumnType[] => [
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('TPP.TPP_NAME_IN_LATIN')}</p>
          <p>B_05.01.0060</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('TPP.TPP_NAME_IN_LATIN_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'third_party_service_provider_name',
    fixed: 'left',
    editable: true,
    className: 'highlighted-column',
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('TPP.LEGAL_NAME')}</p>
          <p>B_05.01.0050</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('TPP.LEGAL_NAME_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'third_party_service_provider_legal_name',
    editable: true,
    className: 'highlighted-column',
  },
  {
    title: t('TPP.DESCRIPTION'),
    dataIndex: 'description',
    editable: true,
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p> {t('TPP.RANK')}</p>
          <p>B_05.02.0050</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation color="red" description={t('TPP.RANK_INFO')} />
      </Styled.TitleComponent>
    ),
    dataIndex: 'rank',
    editable: true,
    className: 'highlighted-column',
    render: (_: any, record: any) => {
      return (
        <Input
          value={record.rank}
          onChange={(e) =>
            handleSave({
              ...record,
              rank: e.target.value,
            } as ITPP)
          }
        />
      );
    },
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('TPP.RECIPIENT_IDENTIFICATION_TYPE')}</p>
          <p>B_05.02.0070</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('TPP.RECIPIENT_IDENTIFICATION_TYPE_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'recipient_identification_type',
    editable: false,
    width: 400,
    className: 'highlighted-column',
    render: (_: any, record: any) => {
      return (
        <CustomSelect
          options={providerCodeTypes}
          value={
            providerCodeTypes.find(
              (option) =>
                String(option.value) ===
                String(record.recipient_identification_type),
            )?.value || undefined
          }
          onChange={(
            value: string,
            option: { label: string; value: string },
          ) => {
            handleSave({
              ...record,
              recipient_identification_type: value,
              recipient_identification_label: option.label,
            } as ITPP);
          }}
          filterOption={(input: string, option: { label: string }) =>
            option?.label?.toLowerCase().includes(input.toLowerCase())
          }
        />
      );
    },
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('TPP.ULTIMATE_PARENT_IDENTIFICATION_CODE')}</p>
          <p>B_05.01.0110</p>
        </div>
        <AdditionalInformation
          color="red"
          description={t('TPP.ULTIMATE_PARENT_IDENTIFICATION_CODE_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'ultimate_parent_identification_code',
    editable: true,
    className: 'highlighted-column',
    width: 400,
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('TPP.RECIPIENT_IDENTIFICATION_CODE')}</p>
          <p>B_05.02.0060</p>
        </div>
        <AdditionalInformation
          color="red"
          description={t('TPP.RECIPIENT_IDENTIFICATION_CODE_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'recipient_identification_code',
    editable: true,
    className: 'highlighted-column',
    render: (_: any, record: any) => {
      return (
        <Input
          value={record.recipient_identification_code}
          onChange={(e) =>
            handleSave({
              ...record,
              recipient_identification_code: e.target.value,
            } as ITPP)
          }
        />
      );
    },
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p> {t('TPP.ICT_PROVIDER_IDENTIFICATION_CODE')}</p>
          <p>B_05.01.0010, B_05.02.0030</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('TPP.ICT_PROVIDER_IDENTIFICATION_CODE_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'ict_service_provider_identification_code',
    editable: true,
    className: 'highlighted-column',
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('TPP.IDENTIFICATION_CODE_TYPE')}</p>
          <p>B_03.02.0030</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('TPP.IDENTIFICATION_CODE_TYPE_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'identification_code_type',
    editable: false,
    className: 'highlighted-column',
    render: (_: any, record: any) => {
      return (
        <CustomSelect
          value={
            providerCodeTypes.find(
              (option) =>
                String(option.value) ===
                String(record.identification_code_type),
            )?.label || record.identification_code_type
          }
          onChange={(value: string, option: { label: string; value: string }) =>
            handleSave({
              ...record,
              identification_code_type: option.label,
              tpp_code_type_id: value,
            } as ITPP)
          }
          options={providerCodeTypes}
          filterOption={(input: string, option: { label: string }) =>
            option?.label?.toLowerCase().includes(input.toLowerCase())
          }
        />
      );
    },
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p> {t('TPP.SUBSTITUTABILITY_OF_PROVIDER')} </p>
          <p>B_07.01.0050</p>
        </div>
        <AdditionalInformation
          color="red"
          description={t('TPP.SUBSTITUTABILITY_OF_PROVIDER_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'substitutability_of_ict_provider_name',
    editable: false,
    className: 'highlighted-column',
    render: (_: any, record: any) => (
      <CustomSelect
        value={record.substitutability_of_ict_provider_name}
        onChange={(value: string, option: { label: string; value: number }) =>
          handleSave({
            ...record,
            substitutability_of_ict_provider_name: option.label,
            substitutability_of_ict_provider_id: option.value,
          } as ITPP)
        }
        options={substitutability}
        filterOption={(input: string, option: { label: string }) =>
          option?.label?.toLowerCase().includes(input.toLowerCase())
        }
      />
    ),
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('TPP.ENTITY_NATURE')}</p>
          <p>B_04.01.0030</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('TPP.ENTITY_NATURE_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'entity_nature',
    editable: false,
    className: 'highlighted-column',
    render: (_, record) => (
      <CustomSelect
        options={natures}
        value={record.entity_nature}
        onChange={(value: any, option: { label: string; value: string }) =>
          handleSave({
            ...record,
            entity_nature: option.label,
          } as ITPP)
        }
        filterOption={(input: string, option: { label: string }) =>
          option?.label?.toLowerCase().includes(input.toLowerCase())
        }
      />
    ),
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('TPP.ULTIMATE_PARENT_IDENTIFICATION_CODE_TYPE')}</p>
          <p>B_05.01.0120</p>
        </div>
        <AdditionalInformation
          color="red"
          description={t('TPP.ULTIMATE_PARENT_IDENTIFICATION_CODE_TYPE_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'ultimate_parent_identification_code_type',
    editable: true,
    className: 'highlighted-column',
    width: 400,
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p> {t('TPP.LAST_AUDIT_DATE')}</p>
          <p>B_07.01.0070</p>
        </div>

        <AdditionalInformation
          color="red"
          description={t('TPP.LAST_AUDIT_DATE_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'last_audit_date',
    editable: false,
    className: 'highlighted-column',
    render: (text: string, record: DataType) => {
      const entityRecord = record as ITPP;
      return (
        <Styled.RelativeDateWrapper>
          <DatePicker
            value={text ? dayjs(text) : null}
            format="YYYY-MM-DD"
            onClick={(e) => e.stopPropagation()}
            onChange={(date) => {
              const formattedDateString = date
                ? dayjs(date).format('YYYY-MM-DD')
                : '';
              const updatedRow: ITPP = {
                ...entityRecord,
                last_audit_date: formattedDateString,
              };
              handleSave(updatedRow);
            }}
          />
        </Styled.RelativeDateWrapper>
      );
    },
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('TPP.ADDITIONAL_IDENTIFICATION_CODE')}</p>
          <p>B_05.01.0030</p>
        </div>
        <AdditionalInformation
          color="red"
          description={t('TPP.ADDITIONAL_IDENTIFICATION_CODE_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'add_ict_service_provider_identification_code',
    editable: true,
    className: 'highlighted-column',
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('TPP.ADDITIONAL_IDENTIFICATION_CODE_TYPE')}</p>
          <p>B_05.01.0040</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('TPP.ADDITIONAL_IDENTIFICATION_CODE_TYPE_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'add_identification_code_type',
    editable: true,
    className: 'highlighted-column',
    width: 400,
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('TPP.NOTICE_PERIOD')}</p>
          <p>B_02.02.0110</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('TPP.NOTICE_PERIOD_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'notice_period',
    editable: true,
    className: 'highlighted-column',
    width: 400,
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('TPP.HEADQUARTERS_COUNTRY')}</p>
          <p>B_05.01.0080</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('TPP.COUNTRY_HEADQUARTERS_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'headquarters_country',
    editable: false,

    render: (_, record) => (
      <CustomSelect
        options={countries}
        value={record.headquarters_country}
        onChange={(value: any) => {
          const selectedCountry = countries.find(
            (country) => country.value === value,
          );

          if (selectedCountry) {
            const countryName = selectedCountry.label;
            i18nIsoCountries.registerLocale(enLocale);
            const isoCode = i18nIsoCountries.getAlpha2Code(countryName, 'en');

            handleSave({
              ...record,
              headquarters_country: selectedCountry?.label || '',
              iso_code: isoCode || '',
            } as ITPP);
          }
        }}
        filterOption={(input: string, option: { label: string }) =>
          option?.label?.toLowerCase().includes(input.toLowerCase())
        }
      />
    ),
  },
  {
    title: <>{t('TPP.SELECTED_COUNTRY_ISO_CODE')}</>,
    dataIndex: 'iso_code',
    editable: false,
    className: 'highlighted-column',
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('TPP.DORA_REGULATOR_CRITICALITY')}</p>
        </div>

        <AdditionalInformation
          color="red"
          description="content will be added"
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'dora_regulator_criticality_name',
    editable: false,
    render: (_: any, record: any) => {
      const selectedOption = doraRegCriticality.find(
        (option) => option.value === record.dora_regulator_criticality_id,
      );

      return (
        <CustomSelect
          value={
            selectedOption
              ? selectedOption.label
              : record.dora_regulator_criticality_name
          }
          onChange={(value: string, option: { label: string; value: string }) =>
            handleSave({
              ...record,
              dora_regulator_criticality_name: option.label,
              dora_regulator_criticality_id: option.value,
            } as ITPP)
          }
          options={doraRegCriticality}
          filterOption={(input: string, option: { label: string }) =>
            option?.label?.toLowerCase().includes(input.toLowerCase())
          }
        />
      );
    },
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('TPP.LOCAL_REGULATOR_CRITICALITY')}</p>
        </div>

        <AdditionalInformation
          color="red"
          description="content will be added"
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'local_regulator_criticality_name',
    editable: false,
    render: (_: any, record: any) => {
      const selectedOption = doraRegCriticality.find(
        (option) => option.value === record.local_regulator_criticality_id,
      );

      return (
        <CustomSelect
          value={
            selectedOption
              ? selectedOption.label
              : record.local_regulator_criticality_name
          }
          onChange={(value: string, option: { label: string; value: string }) =>
            handleSave({
              ...record,
              local_regulator_criticality_name: option.label,
              local_regulator_criticality_id: option.value,
            } as ITPP)
          }
          options={localRegCriticality}
          filterOption={(input: string, option: { label: string }) =>
            option?.label?.toLowerCase().includes(input.toLowerCase())
          }
        />
      );
    },
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('TPP.ALTERNATIVE_PROVIDERS_IDENTIFIED')}</p>
          <p>B_07.01.0110</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('TPP.ALTERNATIVE_PROVIDERS_IDENTIFIED_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'alternative_providers_identified_name',
    editable: false,
    className: 'highlighted-column',
    render: (_: any, record: any) => {
      const selectedOption = alternatives.find(
        (option) => option.value === record.alternative_providers_identified_id,
      );

      return (
        <CustomSelect
          value={
            selectedOption
              ? selectedOption.label
              : record.alternative_providers_identified_name
          }
          onChange={(value: string, option: { label: string; value: string }) =>
            handleSave({
              ...record,
              alternative_providers_identified_name: option.label,
              alternative_providers_identified_id: option.value,
            } as ITPP)
          }
          options={alternatives}
          filterOption={(input: string, option: { label: string }) =>
            option?.label?.toLowerCase().includes(input.toLowerCase())
          }
        />
      );
    },
  },
  // {
  //   title: (
  //     <>
  //       Alternative Exit
  //     </>
  //   ),
  //   dataIndex: 'alternatives_exit_name',
  //   editable: false,
  //   render: (_: any, record: any) => {
  //     const selectedOption = alternatives.find(
  //       (option) => option.value === record.alternatives_exit_id
  //     );

  //     return (
  //       <CustomSelect
  //         value={selectedOption ? selectedOption.label : record.alternatives_exit_name}
  //         onChange={(value: string, option: { label: string; value: string }) =>
  //           handleSave({
  //             ...record,
  //             alternatives_exit_name: option.label,
  //             alternatives_exit_id: option.value,
  //           } as ITPP)
  //         }
  //         options={alternatives}
  //         filterOption={(input: string, option: { label: string }) =>
  //           option?.label?.toLowerCase().includes(input.toLowerCase())
  //         }
  //       />
  //     );
  //   }
  // },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p> {t('TPP.EXIT_PLAN_EXISTS')}</p>
          <p>B_07.01.0080</p>
        </div>
        <AdditionalInformation
          color="red"
          description={t('TPP.EXIT_PLAN_EXISTS_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'exit_plan_exists_name',
    editable: false,
    className: 'highlighted-column',
    render: (_: any, record: any) => {
      const selectedOption = alternatives.find(
        (option) => option.value === record.exit_plan_exists_id,
      );

      return (
        <CustomSelect
          value={
            selectedOption ? selectedOption.label : record.exit_plan_exists_name
          }
          onChange={(value: string, option: { label: string; value: string }) =>
            handleSave({
              ...record,
              exit_plan_exists_name: option.label,
              exit_plan_exists_id: option.value,
            } as ITPP)
          }
          options={alternatives}
          filterOption={(input: string, option: { label: string }) =>
            option?.label?.toLowerCase().includes(input.toLowerCase())
          }
        />
      );
    },
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p> {t('TPP.POSSIBILITY_OF_REINTEGRATION')}</p>
          <p>B_07.01.0090</p>
        </div>
        <AdditionalInformation
          color="red"
          description={t('TPP.POSSIBILITY_OF_REINTEGRATION_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'possibility_of_reintegration_name',
    editable: false,
    className: 'highlighted-column',
    render: (_: any, record: any) => {
      const selectedOption = possibility.find(
        (option) => option.value === record.possibility_of_reintegration_id,
      );

      return (
        <CustomSelect
          value={
            selectedOption
              ? selectedOption.label
              : record.possibility_of_reintegration_name
          }
          onChange={(value: string, option: { label: string; value: string }) =>
            handleSave({
              ...record,
              possibility_of_reintegration_name: option.label,
              possibility_of_reintegration_id: option.value,
            } as ITPP)
          }
          options={possibility}
          filterOption={(input: string, option: { label: string }) =>
            option?.label?.toLowerCase().includes(input.toLowerCase())
          }
        />
      );
    },
  },
  {
    title: (
      <>
        {t('TPP.TPP_VALID_FROM_TO')}
        <Styled.Asterisx>*</Styled.Asterisx>
      </>
    ),
    dataIndex: 'date_range',
    editable: false,
    render: (_: any, record: DataType) => {
      const entityRecord = record as ITPP;
      return (
        <Styled.DateInputs>
          <DatePicker
            value={
              entityRecord.start_date ? dayjs(entityRecord.start_date) : null
            }
            format="YYYY-MM-DD"
            onClick={(e) => e.stopPropagation()}
            onChange={(date) => {
              const formattedDateString = date
                ? dayjs(date).format('YYYY-MM-DD')
                : '';

              const updatedRow: ITPP = {
                ...entityRecord,
                start_date: formattedDateString,
              };

              handleSave(updatedRow);
            }}
          />
          <DatePicker
            value={
              entityRecord.end_date
                ? dayjs(entityRecord.end_date)
                : dayjs('9999-01-01')
            }
            format="YYYY-MM-DD"
            onClick={(e) => e.stopPropagation()}
            onChange={(date) => {
              const formattedDateString = date
                ? dayjs(date).format('YYYY-MM-DD')
                : '';

              const updatedRow: ITPP = {
                ...entityRecord,
                end_date: formattedDateString,
              };

              handleSave(updatedRow);
            }}
          />
        </Styled.DateInputs>
      );
    },
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('TPP.NON_SUBSTITUTABLE_REASON')}</p>
          <p>B_07.01.0060</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('TPP.NON_SUBSTITUTABLE_REASON_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'non_subst_reason_id',
    editable: false,
    className: 'highlighted-column',
    render: (_, record) => (
      <CustomSelect
        options={nonSubstReasons}
        value={record.non_subst_reason_id}
        onChange={(value: any) =>
          handleSave({
            ...record,
            non_subst_reason_id: value,
          } as ITPP)
        }
        filterOption={(input: string, option: { label: string }) =>
          option?.label?.toLowerCase().includes(input.toLowerCase())
        }
      />
    ),
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p> {t('TPP.PERSON_TYPE')}</p>
          <p>B_05.01.0070</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('TPP.PERSON_TYPE_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'person_type_id',
    editable: false,
    className: 'highlighted-column',
    render: (_, record) => (
      <CustomSelect
        options={personTypes}
        value={record.person_type_id}
        onChange={(value: any) =>
          handleSave({
            ...record,
            person_type_id: value,
          } as ITPP)
        }
        filterOption={(input: string, option: { label: string }) =>
          option?.label?.toLowerCase().includes(input.toLowerCase())
        }
      />
    ),
  },
];
